import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import React from "react";
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useNavigate,
} from "react-router-dom";
import "./App.css";
import "./Content.css";
import Footer from "./Footer";
import Home from "./components/home/Home";
import Kontakt from "./components/kontakt/Kontakt";
import Leistungen from "./components/leistungen/Leistungen";
import {
  Akupunktur,
  BiotensegralesTraining,
  BlutabnahmeUndBefundbesprechung,
  DryNeedling,
  FutterberatungUndRationsberechnung,
  Hofmag,
  OsteopathieUndPhysiotherapie,
} from "./components/leistungen/Leistungsseite";
import NotFound from "./components/notfound/NotFound";
import Preise from "./components/preise/Preise";
import ScrollToTop from "./components/scrolltotop/ScrollToTop";
import Datenschutzerklaerung from "./components/rechtliches/Datenschutzerklaerung";
import Impressum from "./components/rechtliches/Impressum";
import UeberMich from "./components/uebermich/UeberMich";
import Logo from "./logo.webp";

const drawerWidth = 320;
const navItems: { title: string; link: string }[] = [
  { title: "Über mich", link: "/ueber-mich" },
  { title: "Kontakt", link: "/kontakt" },
  { title: "Leistungen", link: "/leistungen" },
  { title: "Preise", link: "/preise" },
];

function AppShell() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const theme = useTheme();
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const drawer = (
    <Box
      onClick={handleDrawerToggle}
      sx={{
        textAlign: "center",
      }}
    >
      <Typography variant="h6" sx={{ my: 2 }}>
        Pferdeosteopathie Laskowski
      </Typography>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.title} disablePadding>
            <ListItemButton
              sx={{ textAlign: "center" }}
              onClick={() => navigate(item.link)}
            >
              <ListItemText primary={item.title} />
            </ListItemButton>
          </ListItem>
        ))}
        <Divider />
        <ListItem disablePadding>
          <ListItemButton
            sx={{ textAlign: "center" }}
            onClick={() => navigate("/datenschutzerklaerung")}
          >
            <ListItemText primary="Datenschutzerklärung" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            sx={{ textAlign: "center" }}
            onClick={() => navigate("/impressum")}
          >
            <ListItemText primary="Impressum" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  const container =
    window !== undefined ? () => window.document.body : undefined;

  return (
    <Box sx={{ display: "flex", height: "100%" }}>
      <CssBaseline />
      <AppBar component="nav" color="primary">
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { navbar: "none" }, marginRight: 0 }}
          >
            <MenuIcon />
          </IconButton>
          <Button
            sx={{
              color: theme.palette.primary.contrastText,
              textTransform: "none",
            }}
            onClick={() => navigate("/")}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{ flexGrow: 1, display: "block" }}
            >
              Pferdeosteopathie Laskowski
            </Typography>
          </Button>
          <div className="space-logo-mobile" />
          <img src={Logo} className="logo-mobile" alt="Logo Pferdeosteopathie Laskowski" width={56} height={56} decoding="async" />
          <Box className="navs" sx={{ display: { xs: "none", navbar: "flex" } }}>
            <div></div>
            {navItems.map((item) => (
              <Button
                key={item.title}
                sx={{ color: theme.palette.primary.contrastText }}
                onClick={() => navigate(item.link)}
              >
                {item.title}
              </Button>
            ))}
            <img src={Logo} className="logo-desktop" alt="Logo Pferdeosteopathie Laskowski" width={76} height={76} decoding="async" />
          </Box>
        </Toolbar>
      </AppBar>
      <nav>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", navbar: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: theme.palette.secondary.main,
            },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
      <Box
        component="main"
        sx={{ p: 0, height: "100%", width: "100%" }}
      >
        <ScrollToTop />
        <div id="content">
          <Outlet />
        </div>
        <Footer />
      </Box>
    </Box>
  );
}

const router = createBrowserRouter([
  {
    element: <AppShell />,
    children: [
      { path: "/", element: <Home /> },
      { path: "/home", element: <Navigate to="/" replace /> },
      { path: "/kontakt", element: <Kontakt /> },
      { path: "/ueber-mich", element: <UeberMich /> },
      {
        path: "/leistungen/osteopathie-und-physiotherapie",
        element: <OsteopathieUndPhysiotherapie />,
      },
      {
        path: "/leistungen/biotensegrales-training",
        element: <BiotensegralesTraining />,
      },
      {
        path: "/leistungen/futterberatung-und-rationsberechnung",
        element: <FutterberatungUndRationsberechnung />,
      },
      {
        path: "/leistungen/blutabnahme-und-befundbesprechung",
        element: <BlutabnahmeUndBefundbesprechung />,
      },
      { path: "/leistungen/dry-needling", element: <DryNeedling /> },
      { path: "/leistungen/akupunktur", element: <Akupunktur /> },
      { path: "/leistungen/hofmag-therapie", element: <Hofmag /> },
      { path: "/leistungen", element: <Leistungen /> },
      { path: "/preise", element: <Preise /> },
      { path: "/datenschutzerklaerung", element: <Datenschutzerklaerung /> },
      { path: "/impressum", element: <Impressum /> },
      { path: "*", element: <NotFound /> },
    ],
  },
]);

export default function App() {
  return <RouterProvider router={router} />;
}
